import React, { Suspense, useEffect, useState } from 'react';
import cx from 'classnames';
import { isDesktop, getLocalStorage, notDesktop } from '@zola-helpers/client';
import { ButtonV2, Link, useCopyToClipboard, useForceUpdateOnWindowResize } from '@zola/zola-ui';
import type { ToastAction } from '@zola-helpers/client/dist/types/redux/toasts/toastActionTypes';

import { NAV_DATA } from 'constants/navConstants';
import { DISABLED_MODALS } from 'components/manage/guestlist/addressCollection/helpers/disableModal';

import SecondaryNavV2 from 'components/common/header/SecondaryNavV2';
import Mp4GifEmulator from 'components/common/ui/Mp4GifEmulator';
import ManageAddressCollectionLinkTour from 'components/manage/guestlist/addressCollection/ManageAddressCollectionLinkTour';

import {
  ManageAddressCollectionLinkStateProps,
  ManageAddressCollectionLinkDispatchProps,
} from 'components/manage/guestlist/addressCollection/ManageAddressCollectionLink';

import styles from './ManageAddressCollectionLink.module.less';

type ManageAddressCollectionLinkProps = ManageAddressCollectionLinkStateProps &
  ManageAddressCollectionLinkDispatchProps;

const ManageAddressCollectionLink: React.FC<ManageAddressCollectionLinkProps> = ({
  guestCollectionImg,
  hashId,
  onCopy,
}) => {
  useForceUpdateOnWindowResize();
  const displayValue = `${window.location.host}/addr/${hashId}`;
  const theMagicLink = `${window.location.protocol}//${displayValue}`;
  const [, handleCopy] = useCopyToClipboard();
  const [showTour, setShowTour] = useState(false);
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!getLocalStorage(DISABLED_MODALS.MAGIC_LINK_TOUR)) {
      if (notDesktop()) setShowTour(true);
      else {
        const handleIsSecondaryNavRendered = (): void => setShowTour(true);
        window.addEventListener(
          NAV_DATA.EVENTS.NAV_SECONDARY_RENDERED,
          handleIsSecondaryNavRendered
        );
        return (): void => {
          window.removeEventListener(
            NAV_DATA.EVENTS.NAV_SECONDARY_RENDERED,
            handleIsSecondaryNavRendered
          );
        };
      }
    }
  }, [setShowTour]);

  const onCopyClick = (): ToastAction => {
    handleCopy(theMagicLink);
    return onCopy();
  };

  return (
    <div className={styles.container}>
      {showTour && (
        <Suspense fallback={null}>
          <ManageAddressCollectionLinkTour />
        </Suspense>
      )}
      <SecondaryNavV2 activeLinkId={NAV_DATA.SECONDARY.GUEST_LIST.MAGIC_LINK} />
      <div className={styles.contentContainer}>
        <div className={styles.section}>
          <div className={styles.content}>
            <h1 className={cx(styles.title, 'test_1')}>Your Magic Link</h1>
            <p className={styles.description}>
              Text, DM, or email this link to your guests. They&apos;ll smile, click, and fill out a
              form. Then we&apos;ll update your guest list for you.
            </p>
            <div className={styles.copyContainer}>
              <div className={styles.input}>{displayValue}</div>
              <ButtonV2 className={styles.button} onClick={onCopyClick}>
                Copy Link
              </ButtonV2>
            </div>
            <Link
              href="/wedding/manage/guests/collection-link/configure"
              arrow
              size="small"
              className="guest-collection__edit-link"
            >
              Edit Your Link
            </Link>
          </div>
        </div>
        <div className={cx(styles.section, styles.previewSection)}>
          <div className={styles.preview}>
            {guestCollectionImg ? (
              <img
                className={styles.img}
                src={guestCollectionImg}
                alt="Guest address collection preview"
              />
            ) : (
              <Mp4GifEmulator
                src={`${process.env.PUBLIC_URL}/static-preauth/images/AddressCollection/weDo.mp4`}
                className={styles.img}
              />
            )}
          </div>
          <ButtonV2
            component="a"
            variant="secondary"
            target="_blank"
            rel="noopener noreferrer"
            href={theMagicLink}
            className={styles.previewBtn}
          >
            {isDesktop() ? 'Preview As a Guest' : 'Preview What Your Guests Will See'}
          </ButtonV2>
        </div>
      </div>
    </div>
  );
};

export default ManageAddressCollectionLink;



// WEBPACK FOOTER //
// ./src/components/manage/guestlist/addressCollection/ManageAddressCollectionLink/ManageAddressCollectionLink.tsx